import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/v2/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/v2/ui'
import type { KeywordReport } from '@/lib/api/amazon-listing'

interface KeywordCoverageProps { report: KeywordReport }

export function KeywordCoverage({ report }: KeywordCoverageProps) {
  const { t } = useTranslation()
  const shortTail = report.short_tail ?? []
  const longTail = report.long_tail ?? []
  const inBackend = report.in_backend ?? []
  if (shortTail.length === 0 && longTail.length === 0 && inBackend.length === 0) return null
  const coveragePercent = report.coverage_percent ?? 0
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center justify-between">
          {t('optimizer.keywordCoverage')}
          <Badge variant={coveragePercent >= 80 ? 'default' : 'destructive'}>{coveragePercent}%</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <KeywordList label={t('optimizer.shortTail')} items={shortTail} />
        <KeywordList label={t('optimizer.longTail')} items={longTail} />
        {inBackend.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t('optimizer.inBackend')}:</p>
            <div className="flex gap-1 flex-wrap">
              {inBackend.map((kw) => <Badge key={kw} variant="default" className="text-xs">{kw}</Badge>)}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function KeywordList({ label, items }: { label: string; items: Array<{ keyword: string; used_in?: string[]; status: string }> }) {
  if (!items.length) return null
  return (
    <div>
      <p className="text-xs font-medium mb-1">{label}:</p>
      <div className="space-y-0.5">
        {items.map((kw) => {
          const usedIn = kw.used_in ?? []
          return (
            <div key={kw.keyword} className="flex items-center gap-2 text-sm">
              <span className={kw.status === 'covered' ? 'text-green-600' : 'text-red-500'}>{kw.status === 'covered' ? '●' : '○'}</span>
              <span>{kw.keyword}</span>
              {usedIn.length > 0 && <span className="text-xs text-muted-foreground">{usedIn.join(', ')}</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}
